import { Link, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import {
    AdminTable,
    Breadcrumb,
    EditButton,
    Header,
    LinkButton,
    Loading,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
    ViewButton,
} from '@frontend/components/elements/common';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { GroupBlock } from './GroupBlock';

interface IGroup {
    id: string;
    isActive: boolean;
    name: string;
}

interface IServiceGroupSetting {
    endDate: string;
    id: string;
    service: {
        id: string;
        name: string;
        serviceType: string;
    };
    startDate: string;
}

export function GroupsDetails() {
    assertHasAuthorization(ManagerAuthorization.groupRead);
    const { id: groupId } = useParams() as { id: string };

    const [group, setGroup] = useState<IGroup>();
    const [serviceGroupSettingList, setServiceGroupSettingList] = useState<IServiceGroupSetting[]>([]);

    useEffect(() => {
        loadGroup(groupId);
        loadServiceGroupSettingList(groupId);
    }, [groupId]);

    return (
        <Loading
            item={group}
            loaded={(groupItem) => (
                <PageTemplate sideBarProfile={<GroupBlock group={groupItem} />}>
                    <Breadcrumb
                        items={[
                            { text: translateKey('practitioner.adminSettings') },
                            { text: translateKey('groups.groups'), url: '/groups' },
                            { text: translateKey('groups.details'), url: `/groups/${groupId}` },
                        ]}
                    />
                    <Header
                        content={groupItem.name}
                        extraButton={getExtraButtons()}
                    />

                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('common.name')}</TableHeader>
                                <TableHeader>{translateKey('service.serviceType')}</TableHeader>
                                <TableHeader>{translateKey('common.startDate')}</TableHeader>
                                <TableHeader>{translateKey('common.endDate')}</TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {serviceGroupSettingList.map((item) => (
                                <TableRow key={item.id}>
                                    <TableData>
                                        {hasOneAuthorization([
                                            ManagerAuthorization.servicesRead,
                                            ManagerAuthorization.serviceRightsGroups,
                                        ]) ? (
                                            <Link to={`/services/${item.service.id}`}>{item.service.name}</Link>
                                        ) : (
                                            item.service.name
                                        )}
                                    </TableData>
                                    <TableData>{item.service.serviceType}</TableData>
                                    <TableData>
                                        <NullableFormattedDate value={item.startDate} />
                                    </TableData>
                                    <TableData>
                                        <NullableFormattedDate value={item.endDate} />
                                    </TableData>
                                    <TableData>
                                        {hasOneAuthorization(ManagerAuthorization.groupAddEdit) ? (
                                            <EditButton to={`/serviceGroupSettings/${item.id}?groupId=${groupId}`} />
                                        ) : (
                                            <ViewButton to={`/serviceGroupSettings/${item.id}?groupId=${groupId}`} />
                                        )}
                                    </TableData>
                                </TableRow>
                            ))}
                        </TableBody>
                    </AdminTable>
                </PageTemplate>
            )}
        />
    );

    async function loadGroup(id: string) {
        const result = await fetchJson(`/api/groups/${id}`, 'GET');
        setGroup(result.json);
    }

    async function loadServiceGroupSettingList(id: string) {
        const results = await fetchJson(`/api/serviceGroupSettings?groupId=${id}`, 'GET');
        setServiceGroupSettingList(results.json.items);
    }

    function getExtraButtons() {
        return (
            <>
                <LinkButton
                    color="primary"
                    right
                    to={`/groups/${groupId}/edit`}
                >
                    {hasOneAuthorization([ManagerAuthorization.groupAddEdit, ManagerAuthorization.groupEditFinanceData])
                        ? translateKey('common.editItem', translateKey('groups.group'))
                        : translateKey('common.detailsItem', translateKey('groups.group'))}
                </LinkButton>

                {hasOneAuthorization(ManagerAuthorization.groupAddEdit) && (
                    <LinkButton
                        classNames={['mr-2']}
                        color="primary"
                        icon="icon icon-add"
                        right
                        to={`/serviceGroupSettings/add?groupId=${groupId}`}
                    >
                        {translateKey('common.addItem', translateKey('groupSettings.serviceGroupSettings'))}
                    </LinkButton>
                )}
            </>
        );
    }
}
